import React, { useState } from 'react';
import { Modal, Button, Input, message } from 'antd';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { postRequest } from '../service/index';

const ResetPasswordModal = ({ visible, onCancel, email, onPasswordReset }) => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePassword = (password) => {
    // Minimum 8 characters, at least one uppercase, one lowercase, and one number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  // Password validation checks
  const passwordChecks = {
    minLength: formData.newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.newPassword),
    hasLowercase: /[a-z]/.test(formData.newPassword),
    hasNumber: /\d/.test(formData.newPassword),
    passwordsMatch: formData.newPassword && formData.confirmPassword && formData.newPassword === formData.confirmPassword
  };

  const allChecksPassed = Object.values(passwordChecks).every(check => check === true);

  const handleSubmit = async () => {
    const { newPassword, confirmPassword } = formData;

    if (!newPassword || !confirmPassword) {
      message.error('Please fill in all fields');
      return;
    }

    if (!validatePassword(newPassword)) {
      message.error('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number');
      return;
    }

    if (newPassword !== confirmPassword) {
      message.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // First try companies endpoint
      try {
        const companyResponse = await postRequest('/api/companies/updatepassword', {
          email,
          newPassword
        });

        if (companyResponse.success === false && companyResponse.message === "Company not found") {
          // If company not found, try employees endpoint
          const employeeResponse = await postRequest('/api/employees/updatepassword', {
            email,
            newPassword
          });

          if (employeeResponse.success === true) {
            message.success('Password updated successfully!');
            onPasswordReset();
          } else {
            message.error(employeeResponse.message || 'Failed to update password. Please try again.');
          }
        } else if (companyResponse.success === true) {
          // Company password updated successfully
          message.success('Password updated successfully!');
          onPasswordReset();
        } else {
          message.error(companyResponse.message || 'Failed to update password. Please try again.');
        }
      } catch (companyError) {
        // If companies endpoint fails, try employees endpoint
        const employeeResponse = await postRequest('/api/employees/updatepassword', {
          email,
          newPassword
        });

        if (employeeResponse.success === true) {
          message.success('Password updated successfully!');
          onPasswordReset();
        } else {
          message.error(employeeResponse.message || 'Failed to update password. Please try again.');
        }
      }
    } catch (error) {
      message.error('Failed to update password. Please try again.');
      console.error('Password update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      newPassword: '',
      confirmPassword: ''
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    onCancel();
  };

  return (
    <Modal
      title="Reset Password"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button 
          className='bg-primary text-white' 
          key="submit" 
          type="primary" 
          loading={loading} 
          disabled={!allChecksPassed}
          onClick={handleSubmit}
        >
          Update Password
        </Button>,
      ]}
      centered
    >
      <div className="space-y-4">
        <p className="text-gray-600">
          Create a new password for <strong>{email}</strong>
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                name="newPassword"
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
                size="large"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                size="large"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="text-sm text-gray-500">
          <p className="font-medium mb-2">Password requirements:</p>
          <ul className="space-y-1">
            <li className={`flex items-center gap-2 ${passwordChecks.minLength ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordChecks.minLength ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-gray-400" />
              )}
              At least 8 characters long
            </li>
            <li className={`flex items-center gap-2 ${passwordChecks.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordChecks.hasUppercase ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-gray-400" />
              )}
              Contains at least one uppercase letter
            </li>
            <li className={`flex items-center gap-2 ${passwordChecks.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordChecks.hasLowercase ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-gray-400" />
              )}
              Contains at least one lowercase letter
            </li>
            <li className={`flex items-center gap-2 ${passwordChecks.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordChecks.hasNumber ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-gray-400" />
              )}
              Contains at least one number
            </li>
            {formData.confirmPassword && (
              <li className={`flex items-center gap-2 ${passwordChecks.passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                {passwordChecks.passwordsMatch ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <X className="h-4 w-4 text-red-500" />
                )}
                Passwords match
              </li>
            )}
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default ResetPasswordModal;
